import { Navigate, Outlet } from "react-router";
import { LoadingBlock } from "../../components/ui";
import { useMe } from "../../lib/queries";

/**
 * The gate on the programme section, and the reason it is not a security one.
 *
 * Every route behind this checks the role on the server; a member who reaches
 * one gets a 403 from the API whatever this component does. **This is about
 * confusion, not access.** Without it a member who typed `/admin` — or followed
 * a stale link, or hit back into it — got the whole Programme shell: the
 * heading, the tabs, the section chrome, and then an error where the content
 * should be. Nothing leaked, and it looked exactly like something had.
 *
 * So they are sent back to their own dashboard instead, which is the honest
 * answer: this is not a screen for them.
 *
 * `replace` so the programme URL does not sit in history behind them. Somebody
 * bounced out of here and then pressing back should go where they were, not
 * bounce again.
 */
export function AdminLayout() {
  const me = useMe();

  /*
   * Waited for rather than assumed. Redirecting while the role is unknown would
   * throw an admin out of the section on every cold load — the query resolves a
   * moment after the route renders — and they would see their own dashboard
   * flash past for no reason they could explain.
   */
  if (me.isPending) return <LoadingBlock />;

  const role = me.data?.affiliate.role;
  if (role !== "admin" && role !== "finance") return <Navigate to="/" replace />;

  /*
   * No sub-nav here any more. The section's tabs are the *header's* tabs while
   * you are inside it — see `PROGRAMME_NAV` in `AppLayout` — because two rows
   * of navigation put two contexts on screen at once, and "Payouts" appeared
   * twice a click apart meaning the money you are owed and everybody's money.
   */
  return (
    <div className="space-y-6">
      <Outlet />
    </div>
  );
}
